import { MISSING_SCORE_PREFIX, type CategoricalDimension, type RangeDimension } from './filters';
import type { RosterDomainKey, RosterElStatus, RosterRow } from './rosterRows';
import { COHORT_ORDER, DOMAIN_KEYS, DOMAIN_LABELS } from './rosterRows';
import { ROSTER } from './tokens';

export type PendingDimension = 'criteria' | 'benchmark' | 'participation';

export type DimensionId = CategoricalDimension | RangeDimension | PendingDimension;

export type DimensionValue = { value: string; label: string };

export type DimensionGroup =
  | 'Identity & program'
  | 'Proficiency'
  | 'Flashlight benchmark & data'
  | 'Plans';

type Common = {
  id: DimensionId;
  label: string;
  group: DimensionGroup;
  available: boolean;
  adminOnly?: boolean;
  evidenceColumn?: string;
};

export type CategoricalDimensionDef = Common & { kind: 'categorical' };

export type RangeDimensionDef = Common & {
  kind: 'range';
  scale: { min: number; max: number; step: number };
  hasDomainAxis: boolean;
};

export type DimensionDef = CategoricalDimensionDef | RangeDimensionDef;

const EL_STATUS_VALUES: readonly RosterElStatus[] = ['active', 'monitoring', 'former', 'waived'];

export const DIMENSIONS: readonly DimensionDef[] = [
  { id: 'school', label: 'School', group: 'Identity & program', kind: 'categorical', available: true },
  { id: 'grade', label: 'Grade', group: 'Identity & program', kind: 'categorical', available: true },
  { id: 'teacher', label: 'Teacher', group: 'Identity & program', kind: 'categorical', available: true },
  { id: 'cohort', label: 'Cohort', group: 'Identity & program', kind: 'categorical', available: true },
  { id: 'elStatus', label: 'EL Status', group: 'Identity & program', kind: 'categorical', available: true },
  {
    id: 'yearsInEl',
    label: 'Years in program',
    group: 'Identity & program',
    kind: 'range',
    available: true,
    scale: { min: 0, max: 12, step: 1 },
    hasDomainAxis: false,
    evidenceColumn: 'yearsInEl',
  },
  {
    id: 'compositePL',
    label: 'Composite PL',
    group: 'Proficiency',
    kind: 'range',
    available: true,
    scale: { min: 1, max: 6, step: 0.1 },
    hasDomainAxis: false,
    evidenceColumn: 'compositePL',
  },
  {
    id: 'domainPL',
    label: 'Domain proficiency (L/S/R/W)',
    group: 'Proficiency',
    kind: 'range',
    available: true,
    scale: { min: 1, max: 6, step: 0.1 },
    hasDomainAxis: true,
  },
  {
    id: 'criteria',
    label: 'Criteria status',
    group: 'Proficiency',
    kind: 'categorical',
    available: false,
    evidenceColumn: 'criteria',
  },
  {
    id: 'benchmark',
    label: 'Flashlight benchmark score (S/W)',
    group: 'Flashlight benchmark & data',
    kind: 'range',
    available: false,
    scale: { min: 1, max: 5, step: 1 },
    hasDomainAxis: true,
  },
  {
    id: 'participation',
    label: 'Benchmark participation',
    group: 'Flashlight benchmark & data',
    kind: 'categorical',
    available: false,
    evidenceColumn: 'participation',
  },
  {
    id: 'dataQuality',
    label: 'Data quality',
    group: 'Flashlight benchmark & data',
    kind: 'categorical',
    available: true,
    evidenceColumn: 'dataQuality',
  },
  {
    id: 'planSignoff',
    label: 'Plan sign-off',
    group: 'Plans',
    kind: 'categorical',
    available: true,
    adminOnly: true,
    evidenceColumn: 'signOffs',
  },
];

const PLAN_SIGN_OFF_VALUES: readonly DimensionValue[] = [
  { value: 'not_complete', label: 'Not complete' },
  { value: 'complete', label: 'Complete' },
  { value: 'not_finalized', label: 'Not finalized' },
];

export function dimensionById(id: DimensionId): DimensionDef | undefined {
  return DIMENSIONS.find((dimension) => dimension.id === id);
}

export function availableDimensions({ admin = false }: { admin?: boolean } = {}): DimensionDef[] {
  return DIMENSIONS.filter((dimension) => dimension.available && (admin || !dimension.adminOnly));
}

const EM_DASH = '—';

function gradeOrder(grade: string): number {
  if (grade.trim().toUpperCase() === 'K') return 0;
  const digits = grade.match(/\d+/);
  return digits == null ? Number.MAX_SAFE_INTEGER : Number(digits[0]);
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter((value) => value !== '' && value !== EM_DASH))];
}

function plain(values: string[]): DimensionValue[] {
  return values.map((value) => ({ value, label: value }));
}

export function missingScoreLabel(domain: RosterDomainKey): string {
  return `Missing ${DOMAIN_LABELS[domain]} score`;
}

function missingScoreValues(rows: RosterRow[]): DimensionValue[] {
  const found = new Set(rows.flatMap((row) => row.missingDomains));
  return DOMAIN_KEYS.filter((domain) => found.has(domain)).map((domain) => ({
    value: `${MISSING_SCORE_PREFIX}${domain}`,
    label: missingScoreLabel(domain),
  }));
}

export function enumerateValues(id: DimensionId, rows: RosterRow[]): DimensionValue[] {
  switch (id) {
    case 'school':
      return plain(unique(rows.map((r) => r.school)).sort((a, b) => a.localeCompare(b)));
    case 'grade':
      return plain(unique(rows.map((r) => r.grade)).sort((a, b) => gradeOrder(a) - gradeOrder(b)));
    case 'teacher':
      return plain(unique(rows.flatMap((r) => r.teachers)).sort((a, b) => a.localeCompare(b)));
    case 'cohort':
      return COHORT_ORDER.map((value) => ({ value, label: ROSTER.chip[value].label }));
    case 'elStatus':
      return EL_STATUS_VALUES.map((value) => ({ value, label: ROSTER.chip[value].label }));
    case 'dataQuality':
      return missingScoreValues(rows);
    case 'planSignoff':
      return [...PLAN_SIGN_OFF_VALUES];
    default:
      return [];
  }
}
